import { QuoteApi } from "../api/QuoteApi";
import { CalculationType, QuotingType } from "../state/quote-reducer";
import { BaseQuoteHandler } from "./BaseQuoteHandler";
import { IQuoteHandler } from "./interfaces/IQuoteHandler";
import { YesNo } from "./models/enums";
import { RentalQuoteDetail } from "./models/quote.model";
import { RentalCalculation } from "./models/rental/rental-calculation.model";
import { RentalQuote } from "./models/rental/rental.model";
import { MetricType } from "./types/MetricType";
import { QuoteCalculationStatus } from "./types/QuoteCalculationStatus";
import { QuoteLoadPayload } from "./types/QuoteLoadPayloadType";

export class RentalQuoteHandler
  extends BaseQuoteHandler<RentalQuoteDetail>
  implements IQuoteHandler
{
  public async load(id: number): Promise<QuoteLoadPayload> {
    const result = await super.loadBase(id);
    const { response } = result;

    return {
      id: id,
      calculation: response.out,
      quote: response.in,
      warnings: this.getWarningsFromCalculation(response.out),
      metadata: super.getMetadataFromQuote(response.metadata),
    };
  }

  public async calculate(
    quote: RentalQuote,
    id: number | null
  ): Promise<Partial<QuoteLoadPayload>> {
    quote.quoteId = id;
    const calculation = await super.calculateBase(quote);
    await this.saveMetric(id, "CALC", quote, calculation.out);
    return {
      quote: quote,
      calculation: calculation.out,
      warnings: this.getWarningsFromCalculation(calculation.out),
    };
  }

  public async save(
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToSave = this.formatQuoteForAction(quote, calculation);
    const newQuoteId = await super.saveBase(quoteToSave);

    await this.saveMetric(
      +newQuoteId,
      "SAVED",
      quoteToSave.in,
      quoteToSave.out
    );

    return {
      id: +newQuoteId,
      quote: quoteToSave.in,
      calculation: quoteToSave.out,
      warnings: this.getWarningsFromCalculation(quoteToSave.out),
    };
  }

  public async submit(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<boolean> {
    const quoteToSubmit = this.formatQuoteForAction(quote, calculation);
    quoteToSubmit.out.id = id.toString();

    const hasQuoteBeenSubmitted = await super.submitBase(quoteToSubmit);

    if (hasQuoteBeenSubmitted) {
      await this.saveMetric(
        id,
        "SUBMITTED",
        quoteToSubmit.in,
        quoteToSubmit.out
      );
    }

    return hasQuoteBeenSubmitted;
  }

  public async submitForReview(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ) {
    const quoteToSubmit = this.formatQuoteForAction(quote, calculation);
    quoteToSubmit.out.id = id.toString();

    const hasQuoteBeenSubmitted = await super.submitForReviewBase(
      quoteToSubmit
    );

    if (hasQuoteBeenSubmitted) {
      await this.saveMetric(
        id,
        "SUBMITTED",
        quoteToSubmit.in,
        quoteToSubmit.out
      );
    }

    return hasQuoteBeenSubmitted;
  }

  public async submitException(id: number, status: string | undefined) {
    const hasExceptionSubmitted = await super.submitExceptionBase(id, status);
    return hasExceptionSubmitted;
  }

  public async copy(
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToCopy = this.formatQuoteForAction(quote, calculation, true);
    const newQuoteId = await super.copyBase(quoteToCopy);

    await this.saveMetric(
      +newQuoteId,
      "SAVED",
      quoteToCopy.in,
      quoteToCopy.out
    );

    return {
      id: newQuoteId,
      quote: quoteToCopy.in,
      calculation: quoteToCopy.out,
      warnings: this.getWarningsFromCalculation(quoteToCopy.out),
    };
  }

  public async update(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToUpdate = this.formatQuoteForAction(quote, calculation);
    quoteToUpdate.out.id = id.toString();

    const updatedQuote = await super.updateBase(id, quoteToUpdate);

    await this.saveMetric(
      +quoteToUpdate.out.id,
      "UPDATED",
      quoteToUpdate.in,
      quoteToUpdate.out
    );

    return {
      id: +quoteToUpdate.out.id,
      quote: updatedQuote.in,
      calculation: updatedQuote.out,
      warnings: this.getWarningsFromCalculation(updatedQuote.out),
    };
  }

  public formatQuoteForAction(
    quote: QuotingType,
    calculation: CalculationType,
    isCopy: boolean = false
  ): RentalQuoteDetail {
    if (isCopy && quote && calculation) {
      calculation.id = "";
      quote.sfaccountid = undefined;
      quote.sfdealid = undefined;
      quote.campaignId = undefined;
      quote.campaignName = undefined;
      quote.campaignExpirationDate = undefined;
      quote.campaignIncentive = undefined;
      quote.campaignCode = undefined;
      quote.hasSubmittedQuote = false;
      quote.exceptionPricingOverride = {};
      quote.exceptionPricingOverride.achRequired = "Yes";
      quote.exceptionPricingOverride.achExempt = "Non-exempt";
      if ("submittedDate" in quote) {
        quote as RentalQuote;
        quote.submittedDate = undefined;
      }
    }

    return new RentalQuoteDetail(
      quote as RentalQuote,
      calculation as RentalCalculation
    );
  }

  public async saveMetric(
    id: number | null,
    metric: MetricType,
    quote: RentalQuote,
    calculation: RentalCalculation
  ) {
    if (!id) return;

    const preferredScenarioId =
      quote.scenario?.find((x) => x.preferred === YesNo.Yes)?.scenarioId ?? 0;

    const metricOptions = {
      env: process.env.REACT_APP_ENVIRONMENT ?? "DEV",
      producttype: quote.quoteType,
      quoteproducttype: quote.productType,
      quotetype: metric,
      username: quote.username,
      quoteid: id,
      quoteloanamount:
        calculation.scenarios[preferredScenarioId - 1].loanAmt.toString(),
    };

    await QuoteApi.saveMetric(metricOptions);
  }

  public getWarningsFromCalculation(
    calc: RentalCalculation
  ): QuoteCalculationStatus {
    const warnings = calc.scenarios[0].warnings;

    const calculation =
      calc.validationErrors === "h" ? "" : calc.validationErrors;

    const errors = calc.validationErrors;
    const rateLLPAsFactored = calc.rateLLPAs;
    const feeLLPAsFactored = calc.feeLLPAs;
    const overridePricing = calc.overridePricing;

    const calcStatus: QuoteCalculationStatus = {};

    calculation && (calcStatus.calculation = calculation);
    warnings && (calcStatus.warnings = warnings);
    rateLLPAsFactored && (calcStatus.rateLLPAsFactored = rateLLPAsFactored);
    feeLLPAsFactored && (calcStatus.feeLLPAsFactored = feeLLPAsFactored);
    errors && (calcStatus.errors = errors);
    overridePricing && (calcStatus.overridePricing = overridePricing);

    return calcStatus;
  }
}
